import React, { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import AuthPanel from '../components/auth/AuthPanel';
import { useAuth } from '../context/AuthContext';

const getRedirectPath = (from) => {
  if (!from || typeof from.pathname !== 'string') {
    return '/';
  }

  const path = `${from.pathname}${from.search || ''}${from.hash || ''}`;
  return path === '/login' ? '/' : path;
};

function LoginView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, login, status } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectPath = useMemo(
    () => getRedirectPath(location.state?.from),
    [location.state]
  );

  if (status === 'bootstrapping') {
    return (
      <AuthPanel
        eyebrow="Account"
        title="Checking your session"
        description="We are confirming whether you already have an active sign-in."
      >
        <p className="auth-panel-meta mb-0">If your refresh cookie is still valid, you will be signed in automatically.</p>
      </AuthPanel>
    );
  }

  if (isAuthenticated) {
    return <Navigate replace to={redirectPath} />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await login({ email, password });
      navigate(redirectPath, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Unable to log in right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPanel
      eyebrow={null}
      title="Login"
    >
      {location.state?.authMessage && (
        <div className="alert alert-info mt-4 mb-0" role="alert">
          {location.state.authMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger mt-4 mb-0" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-panel-form" onSubmit={handleSubmit}>
        <div>
          <label className="auth-panel-label" htmlFor="login-email">
            Email
          </label>
          <input
            autoComplete="email"
            className="form-control auth-panel-input"
            id="login-email"
            onChange={(event) => setEmail(event.target.value)}
            required
            type="email"
            value={email}
          />
        </div>

        <div>
          <label className="auth-panel-label" htmlFor="login-password">
            Password
          </label>
          <input
            autoComplete="current-password"
            className="form-control auth-panel-input"
            id="login-password"
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </div>

        <div className="auth-panel-inline-actions">
          <button
            className="btn btn-dark auth-panel-submit"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? 'Signing In...' : 'Login'}
          </button>
        </div>
      </form>

      <p className="auth-panel-meta mb-0">
        If you need an account or access to this site, please contact Frankie.
      </p>
    </AuthPanel>
  );
}

export default LoginView;
